// src/pages/BlogPage.js
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios'; // You'll need to install axios with: npm install axios
import '../styles/BlogPage.css';

const BlogPage = () => {
  const [blogPosts, setBlogPosts] = useState([]);
  const [filteredPosts, setFilteredPosts] = useState([]);
  const [activeCategory, setActiveCategory] = useState('All');
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  useEffect(() => {
    // Fetch blog posts from API server
    const fetchBlogPosts = async () => {
      try {
        setLoading(true);
        const response = await axios.get('/api/blogs');
        setBlogPosts(response.data || []);
        setFilteredPosts(response.data || []);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching blog posts:', err);
        setError('Failed to load blog posts. Please try again later.');
        setLoading(false);
      }
    };
    
    fetchBlogPosts();
  }, []);
  
  useEffect(() => {
    let result = blogPosts;
    
    // Filter by category if not 'All'
    if (activeCategory !== 'All') {
      result = result.filter(post => post.category === activeCategory);
    }
    
    // Filter by search term
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(post => 
        post.title.toLowerCase().includes(term) || 
        post.excerpt.toLowerCase().includes(term)
      );
    }
    
    setFilteredPosts(result);
  }, [blogPosts, activeCategory, searchTerm]);
  
  // Get unique categories from blog posts
  const categories = ['All', ...new Set(blogPosts.map(post => post.category))];
  
  if (loading) {
    return (
      <div className="blog-page">
        <div className="blog-header">
          <div className="container">
            <h1 className="blog-title">Our Blog</h1>
            <p className="blog-description">
              Insights, tips, and expertise to help you navigate the business world
            </p>
          </div>
        </div>
        <div className="container">
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <p>Loading articles...</p>
          </div>
        </div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="blog-page">
        <div className="blog-header">
          <div className="container">
            <h1 className="blog-title">Our Blog</h1>
            <p className="blog-description">
              Insights, tips, and expertise to help you navigate the business world
            </p>
          </div>
        </div>
        <div className="container">
          <div className="error-container">
            <h2>Something went wrong</h2>
            <p>{error}</p>
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <div className="blog-page">
      <div className="blog-header">
        <div className="container">
          <h1 className="blog-title">Our Blog</h1>
          <p className="blog-description">
            Insights, tips, and expertise to help you navigate the business world
          </p>
        </div>
      </div>
      
      <div className="container">
        <div className="blog-filters">
          <div className="category-filter">
            <h3>Categories</h3>
            <ul className="category-list">
              {categories.map(category => (
                <li key={category}>
                  <button 
                    className={`category-button ${activeCategory === category ? 'active' : ''}`}
                    onClick={() => setActiveCategory(category)}
                  >
                    {category}
                  </button>
                </li>
              ))}
            </ul>
          </div>
          
          <div className="search-filter">
            <input
              type="text"
              placeholder="Search articles..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
          </div>
        </div>
        
        <div className="blog-content">
          {filteredPosts.length > 0 ? (
            <div className="blog-grid">
              {filteredPosts.map(post => (
                <div className="blog-card" key={post.id}>
                  <div className="blog-card-image">
                    {post.image ? (
                      <img 
                        src={post.image} 
                        alt={post.title} 
                        className="blog-img"
                      />
                    ) : (
                      <div className="placeholder-image blog-img"></div>
                    )}
                  </div>
                  <div className="blog-card-content">
                    <span className="blog-category">{post.category}</span>
                    <h2 className="blog-card-title">{post.title}</h2>
                    <p className="blog-card-date">{post.date}</p>
                    <p className="blog-card-excerpt">{post.excerpt}</p>
                    <Link to={`/blog/${post.id}`} className="blog-card-link">Read More</Link>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="no-results">
              <h3>No articles found</h3>
              <p>Try adjusting your search or filter criteria</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BlogPage;
